import { defineComponent } from 'vue'
import { FrontendAnalyticsEventName } from 'aven_types'

import throttleMixin from './throttleMixin'

/**
 * Shared base mixin that the Form* input components inherit.
 * Superset of the aven_advisor, aven_auto, aven_my and aven_notary copies: it exposes both
 * `validation` and `validationRules` so either app's call sites resolve, and keeps the
 * filter, autofocus, maxLength and formClasses props that only some of the copies declared.
 *
 * validation-provider is intentionally not registered here. Importing vee-validate from
 * aven_shared binds a second module instance whose rule registry is empty (apps extend rules
 * on their own instance), so validation would silently pass. Each app registers
 * ValidationProvider globally in its own initPlugins instead.
 */
const resolveEventTarget = (eventTarget: any): { value: string; name: string } => {
    if (typeof eventTarget === 'string') {
        return { value: eventTarget, name: '' }
    }
    const element = eventTarget?.target ?? eventTarget
    return { value: element?.value ?? '', name: element?.name ?? '' }
}

interface FormInputProps {
    name: string
    placeholder: string | null
    focusPlaceholder: string | null
    autofocus: boolean
    filter: string | null
}

export const formInputMixin = defineComponent({
    mixins: [throttleMixin],
    props: {
        name: { type: String, required: true },
        value: { type: String, default: null },
        validation: { type: String, default: null },
        validationRules: { type: [String, Object], default: null },
        label: { type: String, default: null },
        formClasses: { type: String, default: null },
        placeholder: { type: String, default: null },
        focusPlaceholder: { type: String, default: null },
        autocomplete: { type: String, default: null },
        autofocus: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        inputType: { type: String, default: 'text' },
        maxLength: { type: Number, default: 128 },
        inputmode: {
            type: String,
            default: 'text',
        },
        filter: {
            type: String,
            default: null,
            validator: function (value) {
                return value === null || ['uppercase', 'lowercase'].includes(value as string)
            },
        },
        validationMode: {
            type: String,
            default: 'lazy',
            validator: function (value) {
                return ['eager', 'aggressive', 'passive', 'lazy'].includes(value as string)
            },
        },
    },
    data: function () {
        return {
            dynamicPlaceholder: (this.$props as FormInputProps).placeholder,
            formId: '',
        }
    },
    mounted() {
        const props = this.$props as FormInputProps

        // Shows a different placeholder on focus
        if (props.focusPlaceholder) {
            const input = this.resolveInputElement()
            if (input) {
                input.onfocus = () => {
                    this.dynamicPlaceholder = props.focusPlaceholder
                }
                input.onblur = () => {
                    this.dynamicPlaceholder = props.placeholder
                }
            }
        }

        if (props.autofocus) {
            this.setFocus()
        }

        this.formId = (this.$el as HTMLElement)?.closest?.('form')?.id || ''
    },
    methods: {
        onFocus(eventTarget: any) {
            this.$emit('focus', resolveEventTarget(eventTarget).value)
        },
        onInput(eventTarget: any) {
            const { filter } = this.$props as FormInputProps
            const { value, name } = resolveEventTarget(eventTarget)
            let filteredValue = value
            if (filter === 'uppercase') {
                filteredValue = filteredValue.toUpperCase()
            }
            if (filter === 'lowercase') {
                filteredValue = filteredValue.toLowerCase()
            }
            this.throttleLogging(`${name}: ${filteredValue}`, this.formId)
            this.$emit('input', filteredValue)
        },
        onBlur(eventTarget: any) {
            const { value, name } = resolveEventTarget(eventTarget)
            this.$emit('blur', value)
            this.$logEvent(FrontendAnalyticsEventName.eventFieldInteraction, {
                type: 'blur',
                value,
                name,
            })
        },
        onClick(event: any) {
            this.$emit('click', event)
        },
        resolveInputElement(): HTMLElement | null {
            const ref = this.$refs.formFieldInput as any
            const element = ref?.$el ?? ref
            if (element instanceof HTMLElement) {
                return element
            }
            return document.getElementById((this.$props as FormInputProps).name)
        },
        blur() {
            this.resolveInputElement()?.blur()
        },
        setFocus() {
            this.resolveInputElement()?.focus()
        },
        applyError(errorMessage: any) {
            const provider = this.$refs.provider as any
            provider.applyResult({
                errors: [errorMessage], // array of string errors
                valid: false, // boolean state
                failedRules: {}, // should be empty since this is a manual error.
            })
        },
    },
})
